import { SelectChangeEvent } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getAxios, route } from "../../utils/endpoints";
import { IState } from "./IState";
import { useNavigate } from "react-router-dom";
import { DropdownValue } from "../../types/DropdownValue";
import { User } from "../../model/User";
import { ForwardLook, Slot } from "../../types/ForwardLook";

type CustomFields = {
  fieldId: number;
  fieldName: string;
  fieldType: "S" | "D";
  required: boolean;
  selectedValue: string;
  dropdownValues: DropdownValue[];
};

const initialState: IState = {
  actualFirstname: "",
  actualLastName: "",
  actualAddress: "",
  actualCity: "",
  actualState: "",
  actualZip: "",
  actualPhone: "",
  actualEmail: "",
  otherInfo: "",
  callStatus: "",
  apptDate: new Date(),
  apptTime: "",
  product: "",
  updVersion: 0,
  newDealerId: 0,
  sendEmail: false,
  saleAmount: "",
  phone2: "",
  phone3: "",
  email2: "",
  saleStatus: "",
  saleDate: new Date(),
  orderDate: new Date(),
  shipDate: new Date(),
  installDate: new Date(),
  nbrId: 0,
  dialedTollfree: "",
  leadOutcome: "",
  status: "U",
  formDisabled: true,
};

export const useEditLeadForm = (leadId: number) => {
  const user = new User();
  const navigate = useNavigate();
  const anchorRef = React.useRef(null);

  const [number, setNumber] = useState<DropdownValue | null>(null);

  const [state, setState] = useState<IState>(initialState);
  const [dropdownItems, setDropdownItems] = useState<DropdownValue[]>([]);
  const [dropdownProduct, setDropdownProduct] = useState<DropdownValue[]>([]);
  const [dropDonwNonBillReason, setDropdonwNonBillReason] = useState<
    DropdownValue[]
  >([]);
  const [dropDownDealerAccess, setDropdownDealerAcces] = useState<
    DropdownValue[]
  >([]);
  const [dropdownNumber, setDropDownNumber] = useState<DropdownValue[]>([]);
  const [dropdownSale, setDropdownSale] = useState<DropdownValue[]>([]);
  const [dropdownApptTimeRange, setDropdownApptTimeRange] = useState<
    DropdownValue[]
  >([]);
  const [isDealerInfoDialogOpen, setIsDealerInfoDialogOpen] =
    useState<boolean>(false);
  const [isAddCoverageDialogOpen, setIsAddCoverageDialogOpen] =
    useState<boolean>(false);
  const [isLookForwardModalOpen, setIsLookForwardModalOpen] =
    useState<boolean>(false);
  const [isNewFieldDialogOpen, setIsNewFieldDialogOpen] =
    useState<boolean>(false);
  const [isViewChangesDialogOpen, setIsViewChangesDialogOpen] =
    useState<boolean>(false);

  const [slots, setSlots] = useState<Slot[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [isLookForwardLoading, setIsLookForwardLoading] =
    useState<boolean>(false);
  const [formFields, setFormFields] = useState<CustomFields[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openMenuOptions, setOpenMenuOptions] = useState<boolean>(false);

  const [snackbarState, setSnackbarState] = useState({
    isOpen: false,
    message: "",
    level: "success",
  });

  const onChangeFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const updatedFormFields = [...formFields];
    updatedFormFields[index] = {
      ...updatedFormFields[index],
      selectedValue: event.target.value,
    };
    setFormFields(updatedFormFields);
  };

  const onChangeFormFieldSelect = (
    event: SelectChangeEvent<unknown>,
    index: number
  ) => {
    const updatedFormFields = [...formFields];
    updatedFormFields[index] = {
      ...updatedFormFields[index],
      selectedValue: event.target.value as string,
    };
    setFormFields(updatedFormFields);
  };

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setState({
      ...state,
      [name]: value,
    });
  };

  const hideSnackbar = () => {
    setSnackbarState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  const handleDealerInfoOpen = () => setIsDealerInfoDialogOpen(true);
  const handleDealerInfoClose = () => setIsDealerInfoDialogOpen(false);

  const handleAddCoverageOpen = () => setIsAddCoverageDialogOpen(true);
  const handleAddCoverageClose = () => setIsAddCoverageDialogOpen(false);

  const handleLookForwardModalOpen = () => setIsLookForwardModalOpen(true);
  const handleLookForwardModalClose = () => setIsLookForwardModalOpen(false);

  const handleViewChangesDialogOpen = () => setIsViewChangesDialogOpen(true);
  const handleViewChangesDialogClose = () => setIsViewChangesDialogOpen(false);

  const handleNewFielDialogOpen = () => {
    setIsNewFieldDialogOpen(true);
  };
  const handleNewFielDialogClose = () => {
    setIsNewFieldDialogOpen(false);
  };

  const onChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      sendEmail: event.target.checked,
    });
  };

  const onChangeSelect = (event: SelectChangeEvent<unknown>) => {
    const { name, value } = event.target;

    setState({
      ...state,
      [name]: value as string,
    });
  };

  const toggleMenuOptions = () => setOpenMenuOptions(!openMenuOptions);

  const onUpdate = async (): Promise<void> => {
    let isValid: boolean = true;

    //Additional fields validation
    formFields
      .filter((field) => field.required)
      .forEach((field) => {
        if (field.selectedValue === "" || field.selectedValue === null) {
          isValid = false;
        }
      });

    //Personal information validation
    if (
      !state.actualFirstname ||
      !state.actualLastName ||
      !state.actualAddress ||
      !state.actualCity ||
      !state.actualState ||
      !state.actualZip ||
      !state.actualPhone ||
      !state.actualEmail
    ) {
      setSnackbarState((prevState) => ({
        isOpen: true,
        message: "Please fill all required fields",
        level: "error",
      }));

      return;
    }

    //Lead Detail
    if (!state.callStatus) {
      setSnackbarState((prevState) => ({
        isOpen: true,
        message: "Please fill all required fields",
        level: "error",
      }));

      return;
    }

    if (!isValid) {
      setSnackbarState((prevState) => ({
        isOpen: true,
        message: "Please fill all required fields",
        level: "error",
      }));

      return;
    }

    try {
      await getAxios(`${route.apiCallDetail.callDetail}`, "PUT", {
        ...state,
        id: leadId,
        saleAmount: state.saleAmount,
        dialedTollFree: number?.id.toString(),
        jsonAdditionalFields: JSON.stringify(formFields),
      });
      setSnackbarState((prevState) => ({
        isOpen: true,
        message: "Lead has been updated",
        level: "success",
      }));
    } catch (error) {
      console.error(error);
      setSnackbarState((prevState) => ({
        isOpen: true,
        message: `Something went wrong`,
        level: "error",
      }));
    }
  };

  const onCancel = () => {
    navigate("/view-leads");
  };

  const onChangeDate = (newDate: Date | null, name: string): void => {
    setState({
      ...state,
      [name]: newDate,
    });
  };

  const callDetails = async () => {
    const response = await getAxios(
      "/call-detail/additional-fields",
      "GET",
      undefined,
      {
        leadId: leadId,
      }
    );

    const data = await response.data;

    setFormFields(data);
  };

  const fetchLead = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await getAxios(
        `${route.apiCallDetail.callDetail}?id=${leadId}`,
        "GET"
      );
      const dropdownResponse = await getAxios(
        route.apiOptions.leadStatus,
        "GET"
      );
      const dropdonwProductResponse = await getAxios(
        route.apiOptions.product,
        "GET"
      );
      const nonBillReasonResponse = await getAxios(
        route.apiOptions.nonBillReason,
        "GET"
      );
      const dealerAccessResponse = await getAxios(
        route.apiOptions.dealerAccess,
        "GET"
      );
      const numberResponse = await getAxios(
        route.apiOptions.subcampaign,
        "GET"
      );
      const saleResponse = await getAxios(route.apiOptions.saleStatus, "GET");
      const apptTimeRangeResponse = await getAxios(
        route.apiOptions.apptTimes,
        "GET"
      );

      const leadData = await response.data;
      const dropdowns: DropdownValue[] = await dropdownResponse.data;
      const productItem: DropdownValue[] = await dropdonwProductResponse.data;
      const nonBillReason: DropdownValue[] = await nonBillReasonResponse.data;
      const dealerAccess: DropdownValue[] = await dealerAccessResponse.data;
      const numbers: DropdownValue[] = await numberResponse.data.map(
        (val: any) => {
          return {
            id: parseInt(val.id),
            ...val,
          };
        }
      );
      const sale: DropdownValue[] = await saleResponse.data;
      const apptTimeRange: DropdownValue[] = await apptTimeRangeResponse.data;
      const dialedTollFree: string = await response.data.dialedTollFree;

      // const dealerId: number = dealersIds.includes(response.data.newDealerId) ? response.data.newDealerId : 0;
      const dealerId: number = response.data.newDealerId;
      const nonBillReasonId: number = response.data.nbrId;

      const foundNumber: DropdownValue | undefined = numbers.find(
        (val) => val.id.toString() === dialedTollFree
      );

      setState((prevState) => ({
        ...leadData,
        saleAmount: leadData.saleAmount,
        newDealerId: dealerId,
        nbrId: nonBillReasonId,
        formDisabled: user.HasForbiddenAccess("Can Edit Lead"),
      }));

      setDropdownItems(dropdowns);
      setDropdownProduct(productItem);
      setDropdonwNonBillReason(nonBillReason);
      setDropdownDealerAcces(dealerAccess);
      setDropDownNumber(numbers);
      setDropdownSale(sale);
      setDropdownApptTimeRange(apptTimeRange);
      setNumber(foundNumber === undefined ? null : foundNumber);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    value: DropdownValue | null,
    name: string
  ) => {
    setNumber({
      id: value!.id,
      description: value!.description,
      sortOrder: value!.sortOrder,
    });
  };

  const onLookForward = async () => {
    setIsLookForwardLoading(true);

    try {
      const response = await getAxios(
        "/call-detail/forward-look",
        "GET",
        undefined,
        {
          zip: state.actualZip,
          dealerId: state.newDealerId,
        }
      );

      const data: ForwardLook[] = await response.data;

      if (!data[0]) {
        setSlots([]);
        setTimeSlots([]);
        return;
      }

      const sData = data.map((val: any) => {
        const date: Date = new Date(val.SDate!);
        const weekday: string = date.toLocaleString("en-US", {
          weekday: "long",
        });
        const day: number = date.getDate();
        const month: number = date.getMonth() + 1;

        return {
          label: `${weekday.substring(0, 3)} ${day}/${month}`,
          content: [val.T1, val.T2, val.T3],
        };
      });

      const tSlots: string[] = [
        data[0].TMS_Descr1,
        data[0].TMS_Descr2,
        data[0].TMS_Descr3,
        data[0].TMS_Descr4,
        data[0].TMS_Descr5,
        data[0].TMS_Descr6,
      ];

      setTimeSlots(tSlots);
      setSlots(sData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLookForwardLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
    callDetails();
  }, []);

  useEffect(() => {
    onLookForward();
  }, [state.newDealerId, state.actualZip]);

  return {
    state,
    isDealerInfoDialogOpen,
    isAddCoverageDialogOpen,
    dropdownItems,
    dropdownProduct,
    dropDonwNonBillReason,
    dropDownDealerAccess,
    dropdownNumber,
    dropdownSale,
    dropdownApptTimeRange,
    formFields,
    number,
    snackbarState,
    isLookForwardModalOpen,
    slots,
    timeSlots,
    isLookForwardLoading,
    isLoading,
    onLookForward,
    handleLookForwardModalClose,
    handleLookForwardModalOpen,
    handleDealerInfoClose,
    handleDealerInfoOpen,
    handleAddCoverageClose,
    handleNewFielDialogOpen,
    handleNewFielDialogClose,
    handleViewChangesDialogClose,
    handleViewChangesDialogOpen,
    isNewFieldDialogOpen,
    isViewChangesDialogOpen,
    handleAddCoverageOpen,
    openMenuOptions,
    toggleMenuOptions,
    anchorRef,
    onChange,
    onChangeCheckBox,
    onChangeSelect,
    onChangeAutocomplete,
    onChangeFormField,
    onChangeFormFieldSelect,
    onUpdate,
    fetchLead,
    hideSnackbar,
    onCancel,
    onChangeDate,
  };
};
